import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { db } from '@/config/firebase';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';

const PlayerMode = () => {
  const { screenId } = useParams();
  const [screen, setScreen] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const timerRef = useRef(null);

  // Fetch screen data
  useEffect(() => {
    if (!screenId) {
      setError('No screen ID provided');
      setLoading(false);
      return;
    }

    const screenDocRef = doc(db, 'screens', screenId);
    const unsubScreen = onSnapshot(screenDocRef, 
      (screenDoc) => {
        if (screenDoc.exists()) {
          setScreen({ id: screenDoc.id, ...screenDoc.data() });
          // Update screen status to online
          updateDoc(screenDocRef, { 
            status: 'online', 
            lastPing: serverTimestamp() 
          }).catch(() => {});
        } else {
          setError('Screen not found');
        }
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    // Heartbeat - update lastPing every minute
    const heartbeat = setInterval(() => {
      updateDoc(screenDocRef, { 
        lastPing: serverTimestamp() 
      }).catch(() => {});
    }, 60000);

    return () => {
      unsubScreen();
      clearInterval(heartbeat);
    };
  }, [screenId]);

  // Fetch playlist when screen changes
  useEffect(() => {
    if (!screen?.playlistId) {
      setPlaylist(null);
      setMediaItems([]);
      return;
    }

    const unsubPlaylist = onSnapshot(doc(db, 'playlists', screen.playlistId),
      (playlistDoc) => {
        if (playlistDoc.exists()) {
          const playlistData = { id: playlistDoc.id, ...playlistDoc.data() };
          setPlaylist(playlistData);
          // Fetch media items
          if (playlistData.mediaIds?.length > 0) {
            Promise.all(
              playlistData.mediaIds.map(id => 
                new Promise((resolve) => {
                  const unsub = onSnapshot(doc(db, 'media', id), (mediaDoc) => {
                    if (mediaDoc.exists()) {
                      resolve({ id: mediaDoc.id, ...mediaDoc.data() });
                    } else {
                      resolve(null);
                    }
                    unsub();
                  });
                })
              )
            ).then(items => {
              setMediaItems(items.filter(Boolean));
            });
          }
        }
      }
    );

    return () => unsubPlaylist();
  }, [screen?.playlistId]);

  // Playback logic
  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % mediaItems.length);
  }, [mediaItems.length]);

  useEffect(() => {
    if (mediaItems.length === 0) return;

    const currentMedia = mediaItems[currentIndex];
    if (!currentMedia) return;

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // For images, set a timer
    if (currentMedia.type === 'image') {
      timerRef.current = setTimeout(goToNext, (playlist?.duration || 10) * 1000);
    }
    // For videos, wait for onEnded event

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentIndex, mediaItems, playlist?.duration, goToNext]);

  // Handle video end
  const handleVideoEnd = () => {
    goToNext();
  };

  if (loading) {
    return (
      <div className="w-screen h-screen bg-black flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen bg-gradient-to-br from-slate-900 to-black flex flex-col items-center justify-center text-white">
        <div className="text-6xl mb-4">⚠️</div>
        <h1 className="text-2xl font-bold mb-2">Screen Error</h1>
        <p className="text-slate-400">{error}</p>
        <p className="text-sm text-slate-500 mt-4">Screen ID: {screenId}</p>
      </div>
    );
  }

  if (!playlist || mediaItems.length === 0) {
    return (
      <div className="w-screen h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 flex flex-col items-center justify-center text-white">
        <div className="text-center">
          <div className="w-24 h-24 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-2">{screen?.name || 'Digital Screen'}</h1>
          <p className="text-slate-400 mb-4">No playlist assigned</p>
          <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span>Connected</span>
          </div>
        </div>
      </div>
    );
  }

  const currentMedia = mediaItems[currentIndex];

  return (
    <div className="w-screen h-screen bg-black overflow-hidden">
      {currentMedia?.type === 'image' ? (
        <img
          src={currentMedia.url}
          alt=""
          className="w-full h-full object-contain"
          style={{ objectFit: screen?.orientation === 'portrait' ? 'contain' : 'cover' }}
        />
      ) : currentMedia?.type === 'video' ? (
        <video
          ref={videoRef}
          src={currentMedia.url}
          autoPlay
          muted
          onEnded={handleVideoEnd}
          className="w-full h-full object-cover"
        />
      ) : null}

      {/* Debug overlay (hidden in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-4 left-4 bg-black/50 text-white text-xs p-2 rounded">
          <p>Screen: {screen?.name}</p>
          <p>Playlist: {playlist?.name}</p>
          <p>Item: {currentIndex + 1}/{mediaItems.length}</p>
        </div>
      )}
    </div>
  );
};

export default PlayerMode;
